import { Injectable, computed, signal } from '@angular/core';
import { updatePreset, updateSurfacePalette } from '@primeng/themes';
import {
    BACKGROUND_SCHEMES,
    DEFAULT_BACKGROUND_SCHEME,
    DEFAULT_LOGO_VARIANT,
    DEFAULT_PRIMARY,
    DEFAULT_SIDEBAR_SCHEME,
    DEFAULT_SURFACE,
    LogoVariantId,
    PrimaryOption,
    SIDEBAR_SCHEMES,
    ThemePresetOption,
    TOKENS_CSS_PRIMARY,
    buildSymBlueMap,
    findBackgroundScheme,
    findLogoVariant,
    findPrimary,
    findSidebarScheme,
    findSurface
} from '@/theme/theme-catalog';

export interface ThemeRuntimeConfig {
    primary: string;
    surface: string;
    sidebarScheme: string;
    backgroundScheme: string;
    logoVariant: LogoVariantId;
}

const DEFAULTS: ThemeRuntimeConfig = {
    primary: DEFAULT_PRIMARY,
    surface: DEFAULT_SURFACE,
    sidebarScheme: DEFAULT_SIDEBAR_SCHEME,
    backgroundScheme: DEFAULT_BACKGROUND_SCHEME,
    logoVariant: DEFAULT_LOGO_VARIANT
};

/**
 * ThemeRuntimeService — único punto que escribe la elección de tema en el DOM.
 *
 * Responsabilidades, y solo estas tres:
 *
 *  1. Preset de PrimeNG — `updatePreset()` / `updateSurfacePalette()`.
 *  2. Tokens de marca — inyecta `<style id="sym-theme-runtime">` remapeando la
 *     escala `--sym-blue-*`. `tokens.css` NO se toca: sigue byte-idéntico al
 *     handoff de marca en `design/`, y al volver a la opción de marca el
 *     `<style>` se retira y sus valores vuelven a mandar.
 *  3. Cromo — clases `sidebar-scheme-*` / `bg-scheme-*` sobre `<html>`, que
 *     `src/styles/brand/_schemes.scss` traduce a variables de layout.
 *
 * Deliberadamente NO gestiona claro/oscuro: eso vive en `LayoutService`
 * (`.app-dark` + `data-theme`, con su propia historia de bugs documentada).
 * Aquí solo se lee ese estado cuando hace falta derivar algo del cromo.
 */
@Injectable({ providedIn: 'root' })
export class ThemeRuntimeService {
    static readonly STORAGE_KEY = 'sym70.theme.config';
    private static readonly STYLE_ELEMENT_ID = 'sym-theme-runtime';

    private readonly config = signal<ThemeRuntimeConfig>(this.readStoredConfig());

    readonly primary = computed(() => this.config().primary);
    readonly surface = computed(() => this.config().surface);
    readonly sidebarScheme = computed(() => this.config().sidebarScheme);
    readonly backgroundScheme = computed(() => this.config().backgroundScheme);
    readonly logoVariant = computed(() => this.config().logoVariant);

    /**
     * El sidebar necesita saber si su cromo es oscuro para elegir la variante
     * del logo — y eso depende del esquema elegido, no del tema de la app: con
     * sidebar Navy hace falta el logo negativo incluso en modo claro.
     */
    readonly sidebarHasDarkChrome = computed(() => findSidebarScheme(this.config().sidebarScheme).darkChrome);

    /**
     * Aplica la configuración completa. Se llama desde `provideAppInitializer`
     * para que el tema guardado esté puesto antes del primer render y no haya
     * flash de color.
     */
    init(): void {
        this.apply(this.config());
    }

    setPrimary(id: string): void {
        this.update({ primary: id });
    }

    setSurface(id: string): void {
        this.update({ surface: id });
    }

    setSidebarScheme(id: string): void {
        this.update({ sidebarScheme: id });
    }

    setBackgroundScheme(id: string): void {
        this.update({ backgroundScheme: id });
    }

    /**
     * La variante de marca no escribe nada en el DOM: la consume `AppSidebar`
     * leyendo la señal. Por eso no aparece en `apply()`.
     */
    setLogoVariant(id: string): void {
        this.update({ logoVariant: findLogoVariant(id).id });
    }

    /**
     * Aplica un preset completo en UNA pasada.
     *
     * No son cuatro `set*` seguidos a propósito: cada `update()` persiste y
     * reaplica todo el tema (incluido `updatePreset()` de PrimeNG), así que
     * encadenarlos provocaría cuatro reconstrucciones y un parpadeo visible.
     *
     * Nota: `dark` del preset NO se toca aquí — lo aplica el configurador vía
     * LayoutService. Ver ThemePresetOption.dark.
     */
    applyPreset(preset: Pick<ThemePresetOption, 'primary' | 'surface' | 'sidebarScheme' | 'backgroundScheme'>): void {
        this.update({
            primary: findPrimary(preset.primary).id,
            surface: findSurface(preset.surface).id,
            sidebarScheme: findSidebarScheme(preset.sidebarScheme).id,
            backgroundScheme: findBackgroundScheme(preset.backgroundScheme).id
        });
    }

    /** Vuelve al look de marca. No toca claro/oscuro: esa es elección aparte. */
    reset(): void {
        this.config.set({ ...DEFAULTS });
        this.persist();
        this.apply(this.config());
    }

    isBrandDefault(): boolean {
        const c = this.config();
        return (
            c.primary === DEFAULTS.primary &&
            c.surface === DEFAULTS.surface &&
            c.sidebarScheme === DEFAULTS.sidebarScheme &&
            c.backgroundScheme === DEFAULTS.backgroundScheme &&
            c.logoVariant === DEFAULTS.logoVariant
        );
    }

    // -----------------------------------------------------------------------

    private update(patch: Partial<ThemeRuntimeConfig>): void {
        this.config.update((state) => ({ ...state, ...patch }));
        this.persist();
        this.apply(this.config());
    }

    private apply(config: ThemeRuntimeConfig): void {
        const primary = findPrimary(config.primary);
        updatePreset(this.buildPresetExt(primary));
        updateSurfacePalette(findSurface(config.surface).palette);
        this.writeBrandTokens(primary);
        this.writeChromeClasses(config);
    }

    // ---- (2) tokens de marca ----------------------------------------------

    private writeBrandTokens(primary: PrimaryOption): void {
        const existing = document.getElementById(ThemeRuntimeService.STYLE_ELEMENT_ID);

        // 'brand' (symBlue) es exactamente lo que ya dice tokens.css: retirar
        // el <style> es más honesto que reescribir los mismos valores, y deja
        // el DOM limpio cuando no hay override activo. OJO: esto es
        // TOKENS_CSS_PRIMARY, no DEFAULT_PRIMARY — el default de la app
        // (`brand-navy`) SÍ necesita su propio override, igual que cualquier
        // otra opción del catálogo.
        if (primary.id === TOKENS_CSS_PRIMARY) {
            existing?.remove();
            return;
        }

        const map = buildSymBlueMap(primary);
        if (!map) {
            // noir: sin rampa propia, el cromo se queda con los azules de marca
            // y solo cambia el primary de PrimeNG (grafito contra superficie).
            existing?.remove();
            return;
        }

        const decls = Object.entries(map)
            .map(([token, value]) => `  ${token}: ${value};`)
            .join('\n');

        /**
         * El bloque `html.app-dark` es obligatorio, no una mejora: los alias
         * dark de `_app-bridge.scss` se declaran ahí y `html.app-dark` (0,1,1)
         * gana a `:root` (0,1,0), así que sin repetirlos el modo oscuro
         * ignoraría el color elegido por completo.
         *
         * Los dos tokens desplazados replican lo que hace el bloque dark de
         * tokens.css: el primario sube un escalón para mantener contraste
         * sobre navy.
         */
        const darkShift = [map['--sym-blue-500'] ? `  --sym-blue-600: ${map['--sym-blue-500']};` : '', map['--sym-blue-600'] ? `  --sym-blue-700: ${map['--sym-blue-600']};` : ''].filter(Boolean).join('\n');

        const css = `:root {\n${decls}\n}\nhtml.app-dark {\n${decls}\n${darkShift}\n}\n`;

        const style = existing ?? document.createElement('style');
        style.id = ThemeRuntimeService.STYLE_ELEMENT_ID;
        style.textContent = css;
        if (!existing) document.head.appendChild(style);
    }

    // ---- (3) clases de cromo ----------------------------------------------

    private writeChromeClasses(config: ThemeRuntimeConfig): void {
        const root = document.documentElement;

        SIDEBAR_SCHEMES.forEach((s) => root.classList.remove(`sidebar-scheme-${s.id}`));
        BACKGROUND_SCHEMES.forEach((s) => root.classList.remove(`bg-scheme-${s.id}`));

        const sidebar = findSidebarScheme(config.sidebarScheme);
        root.classList.add(`sidebar-scheme-${sidebar.id}`);
        root.classList.add(`bg-scheme-${findBackgroundScheme(config.backgroundScheme).id}`);

        // Marcador transversal: hay CSS que necesita saber "el cromo del sidebar
        // es oscuro" sin enumerar los tres esquemas que lo cumplen (el isotipo
        // pasa a máscara monocroma, por ejemplo). Es información derivada, pero
        // derivarla en CSS obligaría a repetir la lista en cada regla.
        root.classList.toggle('sidebar-chrome-dark', sidebar.darkChrome);
    }

    // ---- (1) preset de PrimeNG -------------------------------------------

    /**
     * Extensión semántica del preset activo. Estructura tomada del configurator
     * de Atlantis, con una diferencia: el ancla.
     *
     * La escala de marca pone su tono de acción en el paso 600 (`#3B6FE8`,
     * manual §2.3) y las escalas de Aura en el 500. Mapear ambas al 500 dejaba
     * las opciones de marca un escalón por debajo del azul real de marca.
     */
    private buildPresetExt(option: PrimaryOption) {
        if (!option.palette) return this.noirPresetExt();

        const a = option.anchor;
        const step = (n: number) => `{primary.${n}}`;

        return {
            semantic: {
                primary: option.palette,
                colorScheme: {
                    light: {
                        primary: {
                            color: step(a),
                            contrastColor: '#ffffff',
                            hoverColor: step(a + 100),
                            activeColor: step(a + 200)
                        },
                        highlight: {
                            // El tinte de fondo sí depende del ancla (la rampa
                            // de marca empieza un escalón más arriba), pero el
                            // texto sobre ese tinte cae en 700/800 en ambos
                            // sistemas — así lo fijan tanto Sym70Preset como el
                            // configurator de Atlantis.
                            background: step(a === 600 ? 100 : 50),
                            focusBackground: step(a === 600 ? 200 : 100),
                            color: step(700),
                            focusColor: step(800)
                        }
                    },
                    dark: {
                        primary: {
                            color: step(a - 100),
                            // 950 en la escala de marca es exactamente el
                            // #0B1626 que Sym70Preset fija a mano; para las
                            // escalas de Aura se mantiene el 900 del original.
                            contrastColor: a === 600 ? '{surface.950}' : '{surface.900}',
                            hoverColor: step(a - 200),
                            activeColor: step(a - 300)
                        },
                        highlight: {
                            background: `color-mix(in srgb, ${step(a - 100)}, transparent 84%)`,
                            focusBackground: `color-mix(in srgb, ${step(a - 100)}, transparent 76%)`,
                            color: 'rgba(255,255,255,.87)',
                            focusColor: 'rgba(255,255,255,.87)'
                        }
                    }
                }
            }
        };
    }

    /** noir: el primary se define contra la superficie, sin rampa de color. */
    private noirPresetExt() {
        return {
            semantic: {
                primary: {
                    50: '{surface.50}',
                    100: '{surface.100}',
                    200: '{surface.200}',
                    300: '{surface.300}',
                    400: '{surface.400}',
                    500: '{surface.500}',
                    600: '{surface.600}',
                    700: '{surface.700}',
                    800: '{surface.800}',
                    900: '{surface.900}',
                    950: '{surface.950}'
                },
                colorScheme: {
                    light: {
                        primary: {
                            color: '{primary.950}',
                            contrastColor: '#ffffff',
                            hoverColor: '{primary.800}',
                            activeColor: '{primary.700}'
                        },
                        highlight: {
                            background: '{primary.950}',
                            focusBackground: '{primary.700}',
                            color: '#ffffff',
                            focusColor: '#ffffff'
                        }
                    },
                    dark: {
                        primary: {
                            color: '{primary.50}',
                            contrastColor: '{primary.950}',
                            hoverColor: '{primary.200}',
                            activeColor: '{primary.300}'
                        },
                        highlight: {
                            background: '{primary.50}',
                            focusBackground: '{primary.300}',
                            color: '{primary.950}',
                            focusColor: '{primary.950}'
                        }
                    }
                }
            }
        };
    }

    // ---- persistencia ------------------------------------------------------

    private readStoredConfig(): ThemeRuntimeConfig {
        try {
            const raw = localStorage.getItem(ThemeRuntimeService.STORAGE_KEY);
            if (!raw) return { ...DEFAULTS };
            const parsed = JSON.parse(raw) as Partial<ThemeRuntimeConfig>;
            // Se normaliza contra el catálogo: un id que ya no exista (opción
            // retirada, storage viejo) cae al default en vez de dejar la app
            // con una clase de esquema que ningún CSS define.
            return {
                primary: findPrimary(parsed.primary).id,
                surface: findSurface(parsed.surface).id,
                sidebarScheme: findSidebarScheme(parsed.sidebarScheme).id,
                backgroundScheme: findBackgroundScheme(parsed.backgroundScheme).id,
                logoVariant: findLogoVariant(parsed.logoVariant).id
            };
        } catch {
            return { ...DEFAULTS };
        }
    }

    private persist(): void {
        try {
            localStorage.setItem(ThemeRuntimeService.STORAGE_KEY, JSON.stringify(this.config()));
        } catch {
            /* storage lleno o bloqueado: la sesión sigue, solo no se recuerda */
        }
    }
}
